"use client";

import {
  Form,
  FormCheckbox,
  FormInput,
  FormStatus,
  useFormStatus,
} from "@/components/primitives/Form";
import { Button } from "@/components/primitives/Button";
import { ConsentPrivacyLabel } from "@/components/forms/ConsentPrivacyLabel";
import { getFormsCopy } from "@/content/forms";
import { subscribeNewsletter } from "@/lib/api/endpoints";
import { buildFormConsentRecord } from "@/lib/consent";
import type { Locale } from "@/lib/i18n/config";
import { useLocale } from "@/lib/i18n/provider";
import { newsletterInputSchema } from "@/lib/api/types";
import { withAttribution } from "@/lib/utm";

export function NewsletterForm({ className = "" }: { className?: string }) {
  const { locale } = useLocale();
  const forms = getFormsCopy(locale);

  return (
    <Form
      schema={newsletterInputSchema}
      trackingName="newsletter"
      successMessage={forms.newsletter.successMessage}
      className={className}
      onSubmit={async (values, signal) => {
        const { consentGiven, ...input } = values;
        if (!consentGiven) return;

        const consentRecord = buildFormConsentRecord({
          consentText: forms.newsletter.consentTextRecord,
          locale,
          formSource: "newsletter",
        });

        await subscribeNewsletter(
          withAttribution(
            { ...input, locale },
            { entryPoint: "newsletter", sourceBase: "newsletter" },
          ),
          consentRecord,
          signal,
        );
      }}
    >
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <FormInput
            name="email"
            type="email"
            label={forms.newsletter.emailLabel}
            placeholder={forms.newsletter.emailPlaceholder}
            required
            autoComplete="email"
            className="flex-1"
          />
          <Submit locale={locale} />
        </div>
        <FormCheckbox
          name="consentGiven"
          required
          label={<ConsentPrivacyLabel text={forms.newsletter.consentLabel} />}
        />
        <FormStatus />
      </div>
    </Form>
  );
}

function Submit({ locale }: { locale: Locale }) {
  const forms = getFormsCopy(locale);
  const status = useFormStatus();
  const busy = status === "submitting";
  return (
    <Button type="submit" variant="secondary" size="md" disabled={busy}>
      {busy ? forms.newsletter.busyLabel : forms.newsletter.submitLabel}
    </Button>
  );
}
